import React from 'react';
import { Tag as AntdTag } from 'antd';
import { css, useTheme } from '@emotion/react';
import styled from 'types/theme.types';
import { legacyOnly, newDesignOnly } from 'constants/themes.constants';
import type { TagProps as AntdTagProps } from 'antd/lib/tag';

export const TAG_PRESETS = ['gray', 'blue', 'cyan', 'green', 'lime', 'orange', 'purple', 'red'] as const;

export type TagPreset = (typeof TAG_PRESETS)[number];
export type TagPalette = { text: string; background: string; border: string };

// Pre-ATH-356 values, kept byte-identical so the switch-off app renders exactly as it does today.
// APO-484 deletes them with the branches that read them.
const LEGACY_LIGHT: Record<TagPreset, TagPalette> = {
  gray: { text: '#4D4D4D', background: '#FAFAFA', border: '#E6E6E6' },
  blue: { text: '#3651D8', background: '#EBEEFB', border: '#9AA7EB' },
  cyan: { text: '#0F9595', background: '#E6FFFB', border: '#87E8DE' },
  green: { text: '#488700', background: '#EDF3E5', border: '#A3C380' },
  lime: { text: '#697C20', background: '#FAFEEC', border: '#DDF970' },
  orange: { text: '#E8930C', background: '#FDF4E7', border: '#F3C985' },
  purple: { text: '#722ED1', background: '#F9F0FF', border: '#D3ADF7' },
  red: { text: '#EF5360', background: '#FDEEEF', border: '#F7A9AF' }
};

// What the deleted tinycolor derivation (lighten 15, alpha .16/.32) emitted from LEGACY_LIGHT.
const LEGACY_DARK: Record<TagPreset, TagPalette> = {
  gray: { text: '#D9D9D9', background: '#FFFFFF14', border: '#FFFFFF29' },
  blue: { text: '#7688e4', background: '#7688e429', border: '#7688e452' },
  cyan: { text: '#16dada', background: '#16dada29', border: '#16dada52' },
  green: { text: '#71d300', background: '#71d30029', border: '#71d30052' },
  lime: { text: '#9cb930', background: '#9cb93029', border: '#9cb93052' },
  orange: { text: '#f6b44b', background: '#f6b44b29', border: '#f6b44b52' },
  purple: { text: '#9c6ddf', background: '#9c6ddf29', border: '#9c6ddf52' },
  red: { text: '#f699a1', background: '#f699a129', border: '#f699a152' }
};

type TagTokenSlot = 'text' | 'background' | 'border' | 'icon' | 'text-hover' | 'border-hover' | 'icon-hover';

export const tagToken = (preset: TagPreset, slot: TagTokenSlot): string =>
  `var(--component-tag-color-${preset}-${slot})`;

// tokens.dark.css redeclares every --component-tag-color-* under [data-theme='dark'], so the new
// design resolves both modes from the same string and never reads theme.mode.
const TOKEN_PALETTE = Object.fromEntries(
  TAG_PRESETS.map(preset => [
    preset,
    { text: tagToken(preset, 'text'), background: tagToken(preset, 'background'), border: tagToken(preset, 'border') }
  ])
) as Record<TagPreset, TagPalette>;

export type TagPaletteMode = { isNewDesign: boolean; isDark?: boolean };

// isDark defaults to false because the accent helpers render light values in dark mode today, and
// switch-off parity keeps that.
export const tagPalette = (preset: TagPreset, { isNewDesign, isDark = false }: TagPaletteMode): TagPalette =>
  isNewDesign ? TOKEN_PALETTE[preset] : (isDark ? LEGACY_DARK : LEGACY_LIGHT)[preset];

// `preset` paints in both switch states, so only the call sites that already had one may pass it.
// `newDesignPreset` is the sweep's addition: it paints under the new design and leaves the legacy
// render on whatever `color` or CSS it has today. APO-484 collapses the two after the flip.
export type TagProps = AntdTagProps & { preset?: TagPreset; newDesignPreset?: TagPreset };

// The wrapper zeroes the trailing margin antd's Tag ships with, so a call site that laid out around it
// restores the raw value on the switch-off side.
export const legacyAntdTagMargin = legacyOnly(css`
  margin-inline-end: 8px;
`);

export const Tag: React.FunctionComponent<TagProps> = ({ children, preset, newDesignPreset, color, ...props }) => {
  const { isNewDesign } = useTheme();
  const resolved = isNewDesign ? (newDesignPreset ?? preset) : color ? undefined : preset;

  return (
    <StyledTag {...props} preset={resolved} color={resolved && isNewDesign ? undefined : color}>
      {children}
    </StyledTag>
  );
};

const StyledTag = styled(AntdTag, { shouldForwardProp: prop => prop !== 'preset' })<{ preset?: TagPreset }>`
  margin-inline-end: 0;
  text-align: center;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  ${({ theme, preset, bordered }) => {
    if (!preset) return undefined;

    const palette = tagPalette(preset, { isNewDesign: theme.isNewDesign, isDark: theme.mode === 'dark' });

    return css`
      color: ${palette.text};
      background: ${palette.background};
      border-color: ${bordered === false ? 'transparent' : palette.border};
    `;
  }}

  ${props =>
    props.preset &&
    newDesignOnly(css`
      .ant-tag-close-icon {
        color: ${props.preset === 'gray' ? tagToken('gray', 'icon') : tagToken(props.preset, 'text')};
      }
    `)(props)}

  ${props =>
    props.preset === 'gray' &&
    newDesignOnly(css`
      &:hover {
        color: ${tagToken('gray', 'text-hover')};
        border-color: ${props.bordered === false ? 'transparent' : tagToken('gray', 'border-hover')};

        .ant-tag-close-icon {
          color: ${tagToken('gray', 'icon-hover')};
        }
      }
    `)(props)}
`;
